import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Brain, Zap, TrendingUp, Target, Sparkles, Users } from "lucide-react";

// Split Google Script URL to obscure it slightly in source code
const SCRIPT_BASE = "https://script.google.com/macros/s/";
const SCRIPT_ID = "AKfycbzlTFZ3XPRjgSRNbnYsXJxJlEsJ6Z0b0vGsQIgJrTxofJb59COeLu-5RPFdndrRXPHUow";
const SCRIPT_SUFFIX = "/exec";
const GOOGLE_SCRIPT_URL = SCRIPT_BASE + SCRIPT_ID + SCRIPT_SUFFIX;

export default function BSSPage(): React.ReactElement {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [company, setCompany] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Slides email state
  const [slidesEmail, setSlidesEmail] = useState("");
  const [slidesSubmitting, setSlidesSubmitting] = useState(false);
  const [slidesSubmitted, setSlidesSubmitted] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const checkMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    setIsMobile(checkMobile);
  }, []);

  async function handleSlidesEmailSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSlidesSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const emailValue = formData.get("slidesEmail") as string;

    const data = {
      email: emailValue,
      timestamp: new Date().toISOString(),
      source: "BSS Landing Page - Slides Email Request",
    };

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      setSlidesSubmitted(true);
      // Auto-fill the email in the waitlist form
      setEmail(emailValue);
    } catch (error) {
      console.error("Failed to submit slides email:", error);
    } finally {
      setSlidesSubmitting(false);
    }
  }

  async function handleWaitlistSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      email: formData.get("email"),
      firstName: formData.get("firstName"),
      company: formData.get("company"),
      timestamp: new Date().toISOString(),
      source: "BSS Landing Page - AI Fundamentals Waitlist",
    };

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      setSubmitted(true);
      e.currentTarget.reset();
      setEmail("");
      setFirstName("");
      setCompany("");
    } catch (error) {
      console.error("Failed to submit waitlist form:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="w-full">
      {/* Presentation Slides Section - with header */}
      <section className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xl md:text-2xl text-muted-foreground mb-6">
            Thanks for attending
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/30 rounded-full text-sm font-medium text-purple-900 dark:text-purple-200 mb-8">
            <Sparkles className="w-4 h-4" />
            <span>Exclusive resources for attendees</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Download the Slides
          </h2>
          <Card className="border-2 hover:border-purple-500 transition-colors">
            <CardContent className="p-8">
              <div className="mb-6">
                <a href="/slides.pdf" download className="block cursor-pointer">
                  <img
                    src="/slide-image.png"
                    alt="Presentation Slides Preview"
                    className="w-full rounded-lg mb-4 shadow-md hover:shadow-xl hover:scale-[1.02] transition-all"
                  />
                </a>
              </div>

              {/* Desktop: Just download button */}
              {!isMobile && (
                <Button asChild size="lg" className="w-full md:w-auto">
                  <a href="/slides.pdf" download className="inline-flex">
                    Download PDF Slides
                  </a>
                </Button>
              )}

              {/* Mobile: Download + Email option */}
              {isMobile && (
                <div className="space-y-6">
                  <Button asChild size="lg" className="w-full">
                    <a href="/slides.pdf" download className="inline-flex">
                      Download PDF Slides
                    </a>
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-300 dark:border-gray-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white dark:bg-background px-2 text-muted-foreground">
                        Or
                      </span>
                    </div>
                  </div>

                  {slidesSubmitted ? (
                    <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg p-6 text-center">
                      <div className="text-green-600 dark:text-green-400 text-3xl mb-2">✓</div>
                      <p className="font-semibold mb-1">We'll email you the slides!</p>
                      <p className="text-sm text-muted-foreground">
                        I'll send you the slides shortly
                      </p>
                    </div>
                  ) : (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-lg p-6">
                      <h3 className="font-semibold text-lg mb-3">
                        I'm on my phone, please email it to me
                      </h3>
                      <form onSubmit={handleSlidesEmailSubmit} className="space-y-3">
                        <Input
                          name="slidesEmail"
                          type="email"
                          placeholder="Your email address"
                          required
                          value={slidesEmail}
                          onChange={(e) => setSlidesEmail(e.target.value)}
                          className="h-12 text-base"
                        />
                        <Button
                          type="submit"
                          size="lg"
                          className="w-full"
                          disabled={slidesSubmitting}
                        >
                          {slidesSubmitting ? "Sending..." : "Email Me the Slides"}
                        </Button>
                      </form>
                      <p className="text-xs text-muted-foreground mt-3 text-center">
                        We'll send you the PDF and save your spot below
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Discovery Call Offer Section - MOVED UP with gradient background */}
      <section className="relative py-12 bg-gradient-to-br from-purple-600 via-blue-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center text-white">
              Let's Talk About Your Business
            </h2>

            <div className="mb-6">
              <div className="mb-8 text-center">
                <p className="text-xl font-semibold mb-2 text-white">
                  Special Offer for System Hub Attendees
                </p>
                <p className="text-lg text-white/90">
                  25-minute discovery call to discuss how self-improving systems can transform your business
                </p>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 mb-8">
                <div className="flex items-center gap-4">
                  <span className="text-xl md:text-2xl text-white/60 line-through">
                    $450 AUD
                  </span>
                  <span className="text-4xl md:text-5xl font-bold text-white">
                    $90 AUD
                  </span>
                </div>
                <span className="bg-green-400/30 px-4 py-2 rounded-full text-base md:text-lg font-semibold text-white">
                  Save 80%
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-4">
                  <Target className="w-8 h-8 text-white flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1 text-white">Identify AI Opportunities</h3>
                    <p className="text-sm text-white/90">Map where AI can transform your processes</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Brain className="w-8 h-8 text-white flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1 text-white">Evaluate Fit</h3>
                    <p className="text-sm text-white/90">Determine if deeper engagement makes sense</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <TrendingUp className="w-8 h-8 text-white flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1 text-white">First Steps</h3>
                    <p className="text-sm text-white/90">Walk away with specific next steps for your business</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Zap className="w-8 h-8 text-white flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1 text-white">Map and Incentivize</h3>
                    <p className="text-sm text-white/90">Understand your current state and potential</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center px-4 md:px-0">
              <Button size="lg" className="w-full md:w-auto md:min-w-[400px] text-base md:text-lg py-4 md:py-6 bg-white text-purple-700 hover:bg-white/90" asChild>
                <a href="#" onClick={(e) => e.preventDefault()}>
                  <span className="hidden md:inline">Schedule Your Discovery Call - $90 AUD</span>
                  <span className="md:hidden">Book Discovery Call - $90</span>
                </a>
              </Button>
            </div>
            <p className="text-xs md:text-sm text-center text-white/90 mt-4">
              ⚡ Limited spots for System Hub attendees · Normally $450
            </p>
          </div>
        </div>
      </section>

      {/* AI Fundamentals Course Waitlist */}
      <section className="bg-muted/50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                AI Fundamentals for Business Owners
              </h2>
              <p className="text-xl text-muted-foreground">
                Comprehensive course designed for business owners implementing AI
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="font-bold text-lg mb-4 flex items-center gap-3">
                  <Brain className="w-6 h-6 text-purple-600" />
                  What You'll Master
                </h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">•</span>
                    <span>Why acting now compounds your competitive advantage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">•</span>
                    <span>The 10/80/10 framework for self-improving processes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">•</span>
                    <span>Human vs AI task distribution decisions</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-4 flex items-center gap-3">
                  <Target className="w-6 h-6 text-blue-600" />
                  Practical Implementation
                </h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Implementation strategies that work today</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Building trust through Ask, Check, Trust cycle</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Real-world examples from working businesses</span>
                  </li>
                </ul>
              </div>
            </div>

            <Card className="border-2 border-primary">
              <CardContent className="p-8">
                <div className="bg-primary/10 p-4 rounded-lg mb-6 text-center">
                  <p className="font-semibold mb-1">Course Status: In Development</p>
                  <p className="text-muted-foreground">Launching Q1 2026</p>
                </div>

                <p className="text-lg font-semibold mb-4 text-center">
                  Get free early access when it launches
                </p>

                {submitted ? (
                  <div className="text-center py-8">
                    <div className="bg-green-100 dark:bg-green-900/30 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-green-600 dark:text-green-400 text-4xl">✓</span>
                    </div>
                    <h3 className="text-2xl font-semibold mb-2">
                      You're on the list!
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      We'll notify you when the course launches in Q1 2026
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      Submit Another Email
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                    <Input
                      name="email"
                      type="email"
                      placeholder="Email address *"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 text-base"
                    />
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        name="firstName"
                        type="text"
                        placeholder="First name (optional)"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="h-12 text-base"
                      />
                      <Input
                        name="company"
                        type="text"
                        placeholder="Company name (optional)"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        className="h-12 text-base"
                      />
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full text-lg py-6"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Get Free Early Access"}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      No spam, ever. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Own The Agent Mastermind */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-block bg-purple-100 dark:bg-purple-900/30 px-4 py-2 rounded-full text-sm font-semibold text-purple-900 dark:text-purple-200 mb-4">
              Limited to 15 Members
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Own The Agent
            </h2>
            <p className="text-xl text-muted-foreground">
              Year-long mastermind for building self-improving systems across your entire organization
            </p>
          </div>

          <Card className="border-2 border-purple-500/30 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <p className="text-lg text-muted-foreground mb-2">Annual Investment</p>
                <p className="text-5xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                  €11,999
                </p>
                <p className="text-sm text-muted-foreground">
                  ~€1,000/month · Six-month option available
                </p>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg mb-8">
                <p className="text-center italic text-lg">
                  "For the friendly, ambitious geeks who automatically think 'I could build something for that'"
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    Deep Support
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">•</span>
                      <span>16 one-on-one strategy sessions per year</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">•</span>
                      <span>45 weekly group calls</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">•</span>
                      <span>Annual retreat included (starts 2026)</span>
                    </li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-600" />
                    Who This Is For
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">✓</span>
                      <span>Ready to leap ahead, not inch forward</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">✓</span>
                      <span>Done with theory, hungry for execution</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">✓</span>
                      <span>Building competitive moats, not using tools</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-muted/50 p-6 rounded-lg mb-6">
                <p className="text-sm text-center font-medium">
                  Most members have their first "this just paid for itself" moment within 2-3 weeks.
                  <br />
                  Typical results: 2-4 major implementations and 10+ hours reclaimed weekly in first six months.
                </p>
              </div>

              <Button size="lg" className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700" asChild>
                <a href="/own">Learn More & Apply</a>
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Selective application process · Limited to 15 members globally
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Advisory Services */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-block bg-slate-200 dark:bg-slate-700 px-4 py-2 rounded-full text-sm font-semibold text-slate-900 dark:text-slate-200 mb-4">
                Executive Level
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Strategic AI Advisory
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Board-level guidance for companies navigating AI transformation
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <Target className="w-12 h-12 text-slate-600 dark:text-slate-400 mx-auto mb-4" />
                <h3 className="font-bold mb-2">Roadmaps</h3>
                <p className="text-sm text-muted-foreground">
                  AI adoption strategy and phased implementation plans
                </p>
              </div>

              <div className="text-center">
                <Users className="w-12 h-12 text-slate-600 dark:text-slate-400 mx-auto mb-4" />
                <h3 className="font-bold mb-2">Capability Building</h3>
                <p className="text-sm text-muted-foreground">
                  Team structure and skill development for AI-first organizations
                </p>
              </div>

              <div className="text-center">
                <TrendingUp className="w-12 h-12 text-slate-600 dark:text-slate-400 mx-auto mb-4" />
                <h3 className="font-bold mb-2">Competitive Edge</h3>
                <p className="text-sm text-muted-foreground">
                  Positioning and differentiation in AI-augmented markets
                </p>
              </div>
            </div>

            <Card className="border-2 border-slate-300 dark:border-slate-700">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-3">
                      Looking for board-level AI guidance?
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      20+ years building and scaling businesses, with deep expertise in AI implementation,
                      digital transformation, and strategic positioning.
                    </p>
                    <ul className="space-y-2 text-sm mb-6">
                      <li className="flex items-center gap-2">
                        <span className="text-slate-600 dark:text-slate-400">✓</span>
                        <span>Built & sold 4 businesses (including Google Top 18 Agency)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-slate-600 dark:text-slate-400">✓</span>
                        <span>Teaching AI implementation since 2017</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-slate-600 dark:text-slate-400">✓</span>
                        <span>Trained 20,000+ professionals worldwide</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-slate-600 dark:text-slate-400">✓</span>
                        <span>All advisory positions are on an annual basis, fee dependent upon requirements</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex-shrink-0">
                    <Button size="lg" className="text-lg px-8 py-6 bg-slate-700 hover:bg-slate-800" asChild>
                      <a href="#" onClick={(e) => e.preventDefault()}>
                        Discuss Advisory Role
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Self-Improving Business Vision - MOVED TO END */}
      <section className="relative py-16 bg-gradient-to-br from-purple-600 via-blue-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
              What is a Self-Improving Business?
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all">
                <CardContent className="p-8">
                  <div className="text-white font-semibold mb-3 text-lg">
                    Most businesses ask:
                  </div>
                  <p className="text-2xl font-light italic mb-4 text-white">
                    "How do we use AI to work faster?"
                  </p>
                  <p className="text-white/90">
                    They automate a task. Save time once. Then move on to the next task.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all ring-2 ring-yellow-400">
                <CardContent className="p-8">
                  <div className="text-white font-semibold mb-3 text-lg flex items-center gap-2">
                    <Brain className="w-5 h-5 text-yellow-300" />
                    Self-improving businesses ask:
                  </div>
                  <p className="text-2xl font-bold mb-4 text-white">
                    "How do we build systems that teach themselves to improve?"
                  </p>
                  <p className="text-white/90">
                    They build systems that get better every time they run.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6 text-lg md:text-xl leading-relaxed text-center max-w-4xl mx-auto">
              <p className="text-white/95">
                <strong>The difference is profound.</strong>
              </p>

              <div className="grid md:grid-cols-3 gap-8 my-12">
                <div className="text-center">
                  <Zap className="w-12 h-12 text-white mx-auto mb-4" />
                  <h3 className="font-bold mb-2 text-white">Knowledge Compounds</h3>
                  <p className="text-sm text-white/90">Your team teaches the system, and the system teaches your team</p>
                </div>
                <div className="text-center">
                  <TrendingUp className="w-12 h-12 text-white mx-auto mb-4" />
                  <h3 className="font-bold mb-2 text-white">Processes Optimize</h3>
                  <p className="text-sm text-white/90">Systems get better every time they run, automatically</p>
                </div>
                <div className="text-center">
                  <Target className="w-12 h-12 text-white mx-auto mb-4" />
                  <h3 className="font-bold mb-2 text-white">Curiosity Costs Minutes</h3>
                  <p className="text-sm text-white/90">Not days. Ask better questions, get instant answers</p>
                </div>
              </div>

              <p className="text-xl md:text-2xl font-semibold text-yellow-300">
                Move from "running your business" to "improving the system that runs your business."
              </p>

              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-xl p-8 mt-12">
                <p className="text-xl md:text-2xl font-bold mb-4">
                  The businesses that embrace this thinking now will compound their advantage every single day.
                </p>
                <p className="text-lg text-white/90">
                  The businesses that wait will fall further behind with every decision their competitors make.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
